import React, { useState, useEffect } from 'react';
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';

interface EmailInputProps {
  value?: string;
  onChange?: (value: string) => void;
  onValidationChange?: (isValid: boolean) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  className?: string;
  autoComplete?: string;
}

export const EmailInput: React.FC<EmailInputProps> = ({
  value = '',
  onChange,
  onValidationChange,
  label = 'Email Address',
  placeholder = 'user@example.com',
  required = false,
  disabled = false,
  error,
  className = '',
  autoComplete = 'email',
}) => {
  const [isValid, setIsValid] = useState(true);
  const [isTouched, setIsTouched] = useState(false);
  const [validationMessage, setValidationMessage] = useState('');

  // Enhanced email validation regex
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

  const validateEmail = (email: string): { isValid: boolean; message: string } => {
    if (!email.trim()) {
      return { isValid: !required, message: required ? 'Email is required' : '' };
    }

    // Check basic format
    if (!emailRegex.test(email)) {
      return { isValid: false, message: 'Please enter a valid email address' };
    }

    // Check for common issues
    if (email.length > 254) {
      return { isValid: false, message: 'Email address is too long' };
    }

    const [localPart, domain] = email.split('@');
    
    if (localPart.length > 64) {
      return { isValid: false, message: 'Email address local part is too long' };
    }

    // Check for consecutive dots
    if (email.includes('..')) {
      return { isValid: false, message: 'Email cannot contain consecutive dots' };
    }

    // Check for valid domain
    if (!domain || domain.length < 2) {
      return { isValid: false, message: 'Please enter a valid domain' };
    }

    // Check for common typos in popular domains
    const commonDomains = [
      'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 
      'aol.com', 'icloud.com', 'protonmail.com'
    ];
    
    const domainLower = domain.toLowerCase();
    const suggestions = commonDomains.filter(d => 
      d !== domainLower && levenshteinDistance(d, domainLower) <= 2
    );

    if (suggestions.length > 0) {
      return { 
        isValid: true, 
        message: `Did you mean ${suggestions[0]}?` 
      };
    }

    return { isValid: true, message: '' };
  };

  // Simple Levenshtein distance for typo detection
  const levenshteinDistance = (str1: string, str2: string): number => {
    const matrix = Array(str2.length + 1).fill(null).map(() => Array(str1.length + 1).fill(null));
    
    for (let i = 0; i <= str1.length; i++) matrix[0][i] = i;
    for (let j = 0; j <= str2.length; j++) matrix[j][0] = j;
    
    for (let j = 1; j <= str2.length; j++) {
      for (let i = 1; i <= str1.length; i++) {
        const indicator = str1[i - 1] === str2[j - 1] ? 0 : 1;
        matrix[j][i] = Math.min(
          matrix[j][i - 1] + 1,
          matrix[j - 1][i] + 1,
          matrix[j - 1][i - 1] + indicator
        );
      }
    }
    
    return matrix[str2.length][str1.length];
  };

  useEffect(() => {
    if (isTouched) {
      const validation = validateEmail(value);
      setIsValid(validation.isValid);
      setValidationMessage(validation.message);
      onValidationChange?.(validation.isValid);
    }
  }, [value, isTouched, required]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value.trim();
    onChange?.(newValue);
  };

  const handleBlur = () => {
    setIsTouched(true);
  };

  const handleFocus = () => {
    if (!isTouched) {
      setIsTouched(true);
    }
  };

  const getInputClassName = () => {
    let className = `
      w-full px-3 py-2 border rounded-md
      focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500
      disabled:opacity-50 disabled:cursor-not-allowed
      transition-colors duration-200
    `;

    if (error) {
      className += ' border-red-300 bg-red-50';
    } else if (isTouched && !isValid) {
      className += ' border-red-300 bg-red-50';
    } else if (isTouched && isValid && value) {
      className += ' border-green-300 bg-green-50';
    } else {
      className += ' border-gray-300';
    }

    return className;
  };

  const showValidationIcon = isTouched && value && !error;

  return (
    <div className={`space-y-1 ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <div className="relative">
        <input
          type="email"
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          autoComplete={autoComplete}
          className={getInputClassName()}
        />
        
        {/* Validation Icon */}
        {showValidationIcon && (
          <div className="absolute inset-y-0 right-0 flex items-center pr-3">
            {isValid ? (
              <CheckCircleIcon className="w-5 h-5 text-green-500" />
            ) : (
              <ExclamationCircleIcon className="w-5 h-5 text-red-500" />
            )}
          </div>
        )}
      </div>
      
      {/* Error Message */}
      {error && (
        <p className="text-sm text-red-600 flex items-center">
          <ExclamationCircleIcon className="w-4 h-4 mr-1" />
          {error}
        </p>
      )}
      
      {/* Validation Message */}
      {!error && isTouched && validationMessage && (
        <p className={`text-sm flex items-center ${
          isValid ? 'text-blue-600' : 'text-red-600'
        }`}>
          {isValid ? (
            <span className="text-blue-500 mr-1">💡</span>
          ) : (
            <ExclamationCircleIcon className="w-4 h-4 mr-1" />
          )}
          {validationMessage}
        </p>
      )}
      
      {/* Success Message */}
      {!error && isTouched && isValid && value && !validationMessage && (
        <p className="text-sm text-green-600 flex items-center">
          <CheckCircleIcon className="w-4 h-4 mr-1" />
          Valid email address
        </p>
      )}
    </div>
  );
};